import { h, Component } from 'preact';
import util from 'preact-util';
import { adminFormat } from '../../lib/raceTime';
import { observer } from 'mobx-preact';
import { Text } from 'preact-i18n';

import GpsIntervalChart from './gpsIntervalChart';

@observer
class GpsAnalysisDrawer extends Component {
    constructor(props) {
        super(props);
        this.state = {
            loading: true,
            gpsData: null,
            rawPoints: [],
            avgInterval: '-',
            avgIntervalGsm: '-',
            avgIntervalSat: '-',
            avgSpeed: '-',
            error: null,
        };
    }

    componentDidMount() {
        this.loadGpsData();
    }

    loadGpsData = async () => {
        const { trackerId, raceClassId, markerInfo } = this.props;
        const { raceStore } = this.props.stores;
        const { race } = raceStore;

        try {
            this.setState({ loading: true, error: null });

            // Fetch GPS points for this tracker using the group API
            const response = await util.fetchApi('/api/gpspoints/group', { method: 'GET' }, {
                race: race.id,
                raceClass: raceClassId,
                allPoints: true,
            });

            if (response.status !== 200) {
                throw new Error('Failed to load GPS data');
            }

            // Find the tracker data for this specific tracker
            const trackers = response.data || [];
            const trackerData = trackers.find(t => t.trackerId === trackerId);

            if (!trackerData) {
                // Fall back to markerInfo if no tracker data found
                this.setState({
                    loading: false,
                    gpsData: markerInfo ? {
                        count: markerInfo.count || 0,
                        firstTimestamp: markerInfo.firstTimestamp,
                        timestamp: markerInfo.timestamp,
                        battery: markerInfo.battery,
                        elevation: markerInfo.elevation,
                    } : { count: 0 },
                    avgInterval: '-',
                    avgSpeed: '-',
                });
                return;
            }

            const points = trackerData.points || [];
            const gpsData = this.processGpsData(points, trackerData);

            this.setState({
                loading: false,
                gpsData,
                rawPoints: points,
                avgInterval: this.calculateAvgInterval(points),
                avgIntervalGsm: this.calculateAvgInterval(points, 'gsm'),
                avgIntervalSat: this.calculateAvgInterval(points, 'sat'),
                avgSpeed: this.calculateAvgSpeed(points, 5),
            });
        } catch (err) {
            console.error('Error loading GPS data:', err);
            this.setState({
                loading: false,
                error: 'Failed to load GPS data',
            });
        }
    };

    processGpsData = (points, trackerData) => {
        if (!points || points.length === 0) {
            return {
                count: trackerData?.count || 0,
                firstTimestamp: trackerData?.firstTimestamp,
                timestamp: trackerData?.timestamp,
                battery: trackerData?.battery,
                elevation: trackerData?.elevation,
                gsmCount: 0,
                satCount: 0,
                otherCount: 0,
            };
        }

        // Points are in format: [lat, lng, timestamp, speed, battery, elevation, type]
        const first = points[0];
        const last = points[points.length - 1];

        let gsmCount = 0;
        let satCount = 0;
        let otherCount = 0;
        let gsmFirstTimestamp = null;
        let gsmLastTimestamp = null;
        let satFirstTimestamp = null;
        let satLastTimestamp = null;

        for (let i = 0; i < points.length; i++) {
            const type = points[i][6];
            const ts = points[i][2];
            if (type === 'gsm') {
                gsmCount++;
                if (!gsmFirstTimestamp) gsmFirstTimestamp = ts;
                gsmLastTimestamp = ts;
            } else if (type === 'sat') {
                satCount++;
                if (!satFirstTimestamp) satFirstTimestamp = ts;
                satLastTimestamp = ts;
            } else {
                otherCount++;
            }
        }

        return {
            count: trackerData?.count || points.length,
            firstTimestamp: first[2],
            timestamp: last[2],
            battery: last[4],
            elevation: last[5],
            gsmCount,
            satCount,
            otherCount,
            gsmFirstTimestamp,
            gsmLastTimestamp,
            satFirstTimestamp,
            satLastTimestamp,
        };
    };

    calculateAvgInterval = (points, filterType) => {
        if (!points || points.length < 2) return '-';

        // Filter points by type if specified
        let filtered = points;
        if (filterType) {
            filtered = points.filter(p => p[6] === filterType);
        }
        if (filtered.length < 2) return '-';

        let totalInterval = 0;
        let count = 0;

        for (let i = 1; i < filtered.length; i++) {
            const prevTimestamp = filtered[i - 1][2]; // timestamp is at index 2
            const currTimestamp = filtered[i][2];
            if (prevTimestamp && currTimestamp) {
                const diff = new Date(currTimestamp) - new Date(prevTimestamp);
                totalInterval += diff;
                count++;
            }
        }

        if (count === 0) return '-';
        const avgMs = totalInterval / count;
        const avgSec = Math.round(avgMs / 1000);
        return `${avgSec} sec`;
    };

    // Calculate distance between two GPS coordinates using Haversine formula
    haversineDistance = (lat1, lon1, lat2, lon2) => {
        const R = 6371; // Earth's radius in km
        const dLat = (lat2 - lat1) * Math.PI / 180;
        const dLon = (lon2 - lon1) * Math.PI / 180;
        const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
            Math.sin(dLon / 2) * Math.sin(dLon / 2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        return R * c; // Distance in km
    };

    calculateAvgSpeed = (points, numPoints = 5) => {
        if (!points || points.length < 2) return '-';

        const lastPoints = points.slice(-Math.min(numPoints, points.length));
        if (lastPoints.length < 2) return '-';

        let totalDistance = 0;
        let totalTime = 0;

        for (let i = 1; i < lastPoints.length; i++) {
            const prev = lastPoints[i - 1];
            const curr = lastPoints[i];
            // Format: [lat, lng, timestamp, speed, battery, elevation]
            const lat1 = prev[0];
            const lon1 = prev[1];
            const lat2 = curr[0];
            const lon2 = curr[1];
            const prevTime = new Date(prev[2]);
            const currTime = new Date(curr[2]);

            if (lat1 && lon1 && lat2 && lon2 && prevTime && currTime) {
                const distance = this.haversineDistance(lat1, lon1, lat2, lon2);
                const timeDiff = (currTime - prevTime) / 1000 / 3600; // hours
                totalDistance += distance;
                totalTime += timeDiff;
            }
        }

        if (totalTime === 0) return '-';
        const avgSpeed = totalDistance / totalTime; // km/h
        return `${util.format(avgSpeed, 1)} km/h`;
    };

    render() {
        const { trackerId, markerInfo, stores } = this.props;
        const { raceStore } = stores;
        const { race = {} } = raceStore;
        const { loading, gpsData, rawPoints, avgInterval, avgIntervalGsm, avgIntervalSat, avgSpeed, error } = this.state;
        const { userStore } = this.props.stores;
        const { user } = userStore;
        const darkmode = util.getNestedValue(user, 'settings.darkmode');

        if (loading) {
            return (
                <div class='w-100 d-flex justify-content-center p-5'>
                    <i class='fa-solid fa-spinner fa-spin fa-2x' />
                </div>
            );
        }

        if (error) {
            return (
                <div class='alert alert-danger m-3'>{error}</div>
            );
        }

        const displayData = gpsData || markerInfo || {};

        return (
            <div class='w-100 p-3'>
                <div class='font-weight-lighter px-3 box-header d-flex flex-row justify-content-between'>
                    <Text id='race.gps-analysis'>GPS Analysis</Text>
                    <i class='fa-solid fa-satellite-dish text-info' />
                </div>
                <div class={`d-flex flex-column mx-3 px-3 py-3 rounded-lg ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}>
                    <small>
                        <div class='table-responsive'>
                            <table class='table table-sm table-striped table-hover'>
                                <tbody>
                                    <tr>
                                        <td style='width: 150px;'>Total GPS points</td>
                                        <td><strong>{displayData.count || 0}</strong></td>
                                    </tr>
                                    <tr>
                                        <td>First point received</td>
                                        <td>{adminFormat(displayData.firstTimestamp, race, 'isoDateCompact')}</td>
                                    </tr>
                                    <tr>
                                        <td>Last point received</td>
                                        <td>{adminFormat(displayData.timestamp, race, 'isoDateCompact')}</td>
                                    </tr>
                                    <tr>
                                        <td>Tracking duration</td>
                                        <td>{displayData.firstTimestamp && displayData.timestamp ?
                                            util.secToHms(util.dateDiff(displayData.firstTimestamp, displayData.timestamp).seconds) : '-'}</td>
                                    </tr>
                                    <tr>
                                        <td>Avg interval</td>
                                        <td>{avgInterval}</td>
                                    </tr>
                                    <tr>
                                        <td>Avg speed (last 5 pts)</td>
                                        <td>{avgSpeed}</td>
                                    </tr>
                                    <tr>
                                        <td>Current battery</td>
                                        <td>
                                            <span class={displayData.battery <= 25 ? 'text-danger' : displayData.battery <= 50 ? 'text-warning' : 'text-success'}>
                                                {displayData.battery}%
                                            </span>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>Elevation</td>
                                        <td>{displayData.elevation} m</td>
                                    </tr>
                                    <tr>
                                        <td>Tracker ID</td>
                                        <td><code>{trackerId}</code></td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </small>

                    {(displayData.gsmCount > 0 || displayData.satCount > 0) && (
                        <small>
                            <div class='mt-3 mb-1 fw-bold text-body-secondary'>GSM / Satellite breakdown</div>
                            <div class='table-responsive'>
                                <table class='table table-sm table-striped table-hover'>
                                    <thead>
                                        <tr>
                                            <td style='width: 150px;'></td>
                                            <td><strong>GSM</strong></td>
                                            <td><strong>Satellite</strong></td>
                                            {displayData.otherCount > 0 && <td><strong>Other</strong></td>}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td>Points</td>
                                            <td>{displayData.gsmCount || 0}</td>
                                            <td>{displayData.satCount || 0}</td>
                                            {displayData.otherCount > 0 && <td>{displayData.otherCount}</td>}
                                        </tr>
                                        <tr>
                                            <td>First point</td>
                                            <td>{displayData.gsmFirstTimestamp ? adminFormat(displayData.gsmFirstTimestamp, race, 'isoDateCompact') : '-'}</td>
                                            <td>{displayData.satFirstTimestamp ? adminFormat(displayData.satFirstTimestamp, race, 'isoDateCompact') : '-'}</td>
                                            {displayData.otherCount > 0 && <td>-</td>}
                                        </tr>
                                        <tr>
                                            <td>Last point</td>
                                            <td>{displayData.gsmLastTimestamp ? adminFormat(displayData.gsmLastTimestamp, race, 'isoDateCompact') : '-'}</td>
                                            <td>{displayData.satLastTimestamp ? adminFormat(displayData.satLastTimestamp, race, 'isoDateCompact') : '-'}</td>
                                            {displayData.otherCount > 0 && <td>-</td>}
                                        </tr>
                                        <tr>
                                            <td>Avg interval</td>
                                            <td>{avgIntervalGsm}</td>
                                            <td>{avgIntervalSat}</td>
                                            {displayData.otherCount > 0 && <td>-</td>}
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </small>
                    )}

                    {rawPoints && rawPoints.length > 1 && (
                        <GpsIntervalChart points={rawPoints} stores={stores} />
                    )}
                </div>
            </div>
        );
    }
}

export default GpsAnalysisDrawer;
